// YouTube/Vimeo facade: show a poster + play button, and only build the
// <iframe> when the user clicks it.
//
// WHY A FACADE
// An embedded YouTube player is ~700KB of third-party JS and sets cookies
// before the visitor has done anything. Rendering it on page load would also
// undo the LCP work — the hero already competes for bandwidth. Nothing is
// requested from youtube.com / vimeo.com until the click.
//
// Self-hosted MP4s don't come through here: they're a native <video
// preload="none">, which the browser already defers.

const YT_HOST = 'https://www.youtube-nocookie.com/embed/';
const VIMEO_HOST = 'https://player.vimeo.com/video/';

// Statamic's embed_url modifier converts full page URLs, but passes a bare
// video ID straight through. Normalise both here so the CMS accepts either.
function embedUrl(value, provider) {
    if (!value) return '';
    if (value.includes('/')) return value;           // already a URL
    return (provider === 'Vimeo' ? VIMEO_HOST : YT_HOST) + value;
}

function videoId(url) {
    if (!url) return '';
    // Last path segment, minus any query string.
    return url.split('?')[0].split('/').filter(Boolean).pop() || '';
}

// YouTube exposes predictable thumbnails; Vimeo needs an API call, so we only
// auto-poster YouTube. A Vimeo block without a poster just shows the play
// button on the block background — set a poster in the CMS for a nicer result.
function autoPoster(img, url, provider) {
    if (provider === 'Vimeo') return;
    const id = videoId(url);
    if (!id) return;
    img.src = `https://i.ytimg.com/vi/${id}/maxresdefault.jpg`;
    // maxres doesn't exist for every upload; fall back to the always-present one.
    img.addEventListener('error', () => {
        img.src = `https://i.ytimg.com/vi/${id}/hqdefault.jpg`;
    }, { once: true });
}

function play(facade, url) {
    const iframe = document.createElement('iframe');
    iframe.className = 'video-embed__iframe';
    // autoplay: the click already was the user gesture, so this is allowed.
    iframe.src = url + (url.includes('?') ? '&' : '?') + 'autoplay=1';
    iframe.allow = 'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share';
    iframe.allowFullscreen = true;
    iframe.title = 'Video player';
    iframe.loading = 'lazy';
    facade.innerHTML = '';
    facade.appendChild(iframe);
    facade.dataset.playing = 'true';
}

export function initVideoFacades() {
    document.querySelectorAll('[data-video-facade]').forEach((facade) => {
        if (facade.dataset.facadeBound) return;
        facade.dataset.facadeBound = 'true';

        const provider = facade.dataset.provider || 'YouTube';
        const url = embedUrl(facade.dataset.embedUrl, provider);
        if (!url) return;

        const auto = facade.querySelector('[data-auto-poster]');
        if (auto) autoPoster(auto, url, provider);

        const button = facade.querySelector('.video-embed__play');
        if (!button) return;

        button.addEventListener('click', () => play(facade, url));
    });
}
